import { ReactElement, useEffect, useState } from 'react'
import { connect, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { history } from '../../../../../store'
import { find } from 'lodash'

import Operations from '../../../../../operations'
import Selectors from '../../../../../selectors'
import { getJSON } from '../../../../../common/services/api'
import { downloadPdfData } from '../../../../../common/utils'
import {
  Badge,
  EmptyState,
  LabelIcon,
  NoDataIcon,
  ListItem,
  Modal,
  theme,
  PageHeader,
} from '@lmc/ui-kit'
import {
  AddConsentDropdownButton,
  ConsentFormModal,
  ConsentPopover,
  ListItemRightChildren,
  ConsentPaywall,
} from './components'
import {
  ConsentFormTemplate,
  CreateConsentFormsJSON,
  RouterProps,
} from '@lmc/common/types'
import { ConsentType } from '@lmc/common/types'
import { Spinner } from '../../../../../components/generic'
import { colors } from '@lmc/common/constants'
import { getResidentConsentFormsTemplates } from './components/ConsentFormModal/utils'

import { withRouter } from '../../../../../withRouter'
import styled from 'styled-components'

import { isBrowser, isTablet } from 'react-device-detect'
import { ProTag } from 'components/ProTag'
import { Resident, HomeDto } from '@lmc/ts-common'
import { handleGPCInvalidate } from '../../ResidentProfileScreen/screens/GPConnectScreen/hooks/useGPdata'
import { useQueryClient } from 'react-query'

const ModalStyled = styled(Modal)`
  .ant-modal-content {
    padding: 27px !important;
  }

  .ant-modal-content {
  }
`

type ResidentConsentFormsScreenProps = {
  archiveConsentForm: (consentFormID: string, successMsg: string) => void
  consentForms: {
    titleContent: string
    subTitleContent: string
    iconContent: string
    state: 'signed' | 'unsigned' | 'archived' | 'refused'
    id: string
    resident?: string
  }[]
  signatureItem: {
    id: string
    category: string
  }
  resident: Resident
  templates: ConsentFormTemplate[]
  fetchResidentConsentForms: (resident: string) => void
  createConsentForms: (consentForms: CreateConsentFormsJSON) => Promise<any>
  hasCarePlans: boolean
  router: RouterProps
}
const isDesktop = isBrowser || isTablet

export const ResidentConsentFormsScreen = ({
  archiveConsentForm,
  consentForms = [],
  createConsentForms,
  fetchResidentConsentForms,
  resident,
  templates,
  signatureItem,
  hasCarePlans,
  router,
}: ResidentConsentFormsScreenProps): ReactElement => {
  const [isModalConsentVisible, setIsModalConsentVisible] =
    useState<boolean>(false)

  // Possibly to rework to more advanced flow
  const isLoading = !consentForms?.every(
    (item) => item.resident === resident?.id
  )

  useEffect(() => {
    if (resident && resident.id) fetchResidentConsentForms(resident.id)
  }, [resident, isModalConsentVisible])

  const { t } = useTranslation()

  const translate = (key: string, variable?: any) =>
    t(`consent.createConsentForms.${key}`, variable)

  const listItemLocalisation = {
    signed: translate('components.listItem.signed'),
    refused: translate('components.listItem.refused'),
    archived: translate('components.listItem.archived'),
    unsigned: translate('components.listItem.unsigned'),
    view: translate('components.listItem.view'),
    edit: translate('components.listItem.edit'),
    archive: translate('components.listItem.archive'),
  }

  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  const [consentType, setConsentType] = useState<ConsentType>('consent')

  const [formId, setFormId] = useState<string | undefined>(undefined)

  const [isPDFDownloading, setPDFDownloading] = useState<boolean>(false)

  const queryClient = useQueryClient()

  const handleArchive = (id: string) => {
    setIsModalVisible(true)
    setFormId(id)
    handleGPCInvalidate(queryClient)
  }

  const handleExportPDF = (id: string, title: string) => {
    const pdfUrl = `residents/${resident?.id}/pdf/consent/${id}`
    const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

    const pdfTitle = `${resident?.name?.first} ${resident?.name?.last}'s ${title}`

    setPDFDownloading(true)

    getJSON({ url, responseType: 'arrayBuffer' }).then((res) => {
      downloadPdfData(res, pdfTitle)
      setPDFDownloading(false)
    })
  }

  const handleView = (id: string) => {
    history.push(`/residents/${resident.id}/consent/${id}`)
    setFormId(id)
  }

  if (!hasCarePlans) {
    return <ConsentPaywall />
  }

  if (!resident) return <Spinner />

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )
  const isGpConnectEnabled = !!home?.odsCode && !!home?.asidCode

  return (
    <div style={styles.wrapper}>
      <ConsentFormModal
        closeMainModal={() => setIsModalConsentVisible(false)}
        fetchResidentConsentForms={fetchResidentConsentForms}
        isModalConsentVisible={isModalConsentVisible}
        resident={resident}
        sendConsentForms={createConsentForms}
        setIsModalConsentVisible={setIsModalConsentVisible}
        templates={templates?.filter((consent) => {
          const isValidConsent =
            consent.type === consentType && !consent.isArchived
          if (isGpConnectEnabled) {
            return isValidConsent
          }
          return isValidConsent && !consent.title.includes('GP Connect')
        })}
        item={signatureItem?.id}
        category={signatureItem?.category}
        consentType={consentType}
      />
      <ModalStyled
        localisation={{
          title: translate('components.modal.warning'),
          confirmText: t(`general.buttons.confirm`),
          cancelText: t(`general.buttons.cancel`),
        }}
        onConfirm={() => {
          // @ts-ignore
          archiveConsentForm(formId, translate('components.modal.wasArchived'))
          setIsModalVisible(false)
        }}
        onCancel={() => {
          setIsModalVisible(false)
          setFormId(undefined)
        }}
        setIsModalVisible={setIsModalVisible}
        isModalVisible={isModalVisible}
      >
        <>
          <div style={styles.archiveModalRow1}>
            {translate('components.modal.row1')}
          </div>
          <div style={styles.archiveModalRow2}>
            {translate('components.modal.row2')}
          </div>
        </>
      </ModalStyled>
      <PageHeader
        sticky
        onBack={
          !isDesktop
            ? () => history.replace?.(`/residents/${router.params.id}/profile`)
            : undefined
        }
        title={translate('title', {
          variable: `${resident?.name?.first} ${resident?.name?.last}`,
        })}
      >
        <ProTag />
        <AddConsentDropdownButton
          addConsentForm={() => setIsModalConsentVisible(true)}
          addCapacityForm={() => setIsModalConsentVisible(true)}
          localisation={{
            addConsentForm: translate('components.dropdownBtn.addConsentForm'),
            addCapacityForm: translate(
              'components.dropdownBtn.addCapacityForm'
            ),
          }}
          setConsentType={setConsentType}
        />
      </PageHeader>
      <div style={{ marginTop: 24 }}>
        {consentForms?.length && !isLoading ? (
          consentForms.map((item) => (
            <ListItem
              key={item.id}
              iconContent={item.iconContent}
              state={item.state}
              onClick={() => handleView(item.id)}
              rightChildren={
                isPDFDownloading && item.state !== 'archived' ? (
                  <Spinner style={{ paddingTop: 0 }} />
                ) : (
                  <ListItemRightChildren
                    localisation={listItemLocalisation}
                    popOver={
                      <ConsentPopover
                        archive={
                          item.state !== 'archived' ? handleArchive : undefined
                        }
                        consentFormTitle={item?.titleContent}
                        generatePDF={handleExportPDF}
                        id={item?.id}
                        view={handleView}
                      />
                    }
                    state={item.state}
                  />
                )
              }
              style={styles.listItem}
              titleContent={
                item.state === 'signed' ? (
                  <>
                    {item.titleContent}
                    <Badge
                      color={colors.green}
                      size="small"
                      style={styles.badge}
                      text={listItemLocalisation.signed}
                      textColor={colors.white}
                    />
                  </>
                ) : item.state === 'refused' ? (
                  <>
                    {item.titleContent}
                    <Badge
                      color={colors.red}
                      size="small"
                      style={styles.badge}
                      text={listItemLocalisation.refused}
                      textColor={colors.white}
                    />
                  </>
                ) : item.state === 'archived' ? (
                  <>
                    {item.titleContent}
                    <div style={styles.archived}>
                      {listItemLocalisation.archived}
                    </div>
                  </>
                ) : (
                  item.titleContent
                )
              }
              subTitleContent={item.subTitleContent}
            />
          ))
        ) : (
          <EmptyState
            title={translate('emptyState')}
            icon={<NoDataIcon />}
            description={`${translate('youHavent')} ${resident?.name?.first} ${
              resident?.name?.last
            }.`}
          />
        )}
      </div>
      <LabelIcon
        iconColor={theme.color.status.primary}
        iconName="QuestionCircleOutlined"
        onPress={() =>
          window.open(
            'http://support.logmycare.co.uk/en/articles/6014548-how-do-i-add-and-sign-consent-and-lack-of-capacity-forms',
            '_blank'
          )
        }
        style={styles.helpLink}
        text="Help"
        textColor={theme.color.status.primary}
      />
    </div>
  )
}

const styles = {
  addFormsSection: {
    margin: '20px 0px 40px 0px',
  },
  archived: {
    fontSize: 12,
    fontWeight: 300,
    color: colors.red,
    marginLeft: 8,
    marginTop: 4,
  },
  archiveModalRow1: {
    fontSize: 14,
  },
  archiveModalRow2: {
    fontWeight: 700,
    fontSize: 14,
  },
  badge: {
    width: 'fit-content',
  },
  dropdownBtnContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingBottom: 20,
  },
  header: {
    paddingBottom: 24,
  },
  helpLink: {
    marginTop: 32,
  },
  listItem: {
    marginBottom: 8,
  },
  popOver: {
    fontSize: 9,
  },
  popOverItem: {
    cursor: 'pointer',
    margin: '4px 0px',
  },
  subHeader: {
    marginBottom: 20,
  },
  wrapper: {
    padding: '24px 0px 48px',
    margin: '0px auto',
    maxWidth: 1080,
  },
}

const mapStateToProps = (
  state: {
    data: {
      templates: ConsentFormTemplate[]
      residents: Resident[]
    }
    global: {
      home: {
        carePlans: boolean
      }
    }
  },
  props: any
) => {
  const consentForms = Selectors.getResidentConsentForms(state)
  return {
    consentForms: Selectors.getResidentConsentForms(state),
    resident: find(state.data.residents, { id: props.router.params.id }),
    // @ts-ignore
    signatureItem: find(state.data['log-category-items'], {
      itemId: 'signature',
    }),
    templates: getResidentConsentFormsTemplates(
      // @ts-ignore
      state?.data['consent-templates'],
      consentForms
    ),
    hasCarePlans: state.global.home.carePlans,
  }
}

const mapDispatchToProps = (dispatch: any) => ({
  archiveConsentForm: (consentFormID: string, successMsg: string) => {
    dispatch(
      Operations.updateListItem({
        listId: 'consent-forms',
        itemId: consentFormID,
        json: { isArchived: true },
        successMsg,
      })
    )
  },
  createConsentForms: (consentForms: CreateConsentFormsJSON) =>
    dispatch(
      Operations.createListItem({
        listId: 'consent-forms',
        itemId: 'sign',
        json: consentForms,
      })
    ),
  fetchResidentConsentForms: (resident: any) => {
    dispatch(Operations.loadList('consent-forms', { resident }))
    dispatch(Operations.loadList('consent-templates'))
  },
})

export default withRouter(
  connect<any, any, any>(
    // @ts-ignore
    mapStateToProps,
    mapDispatchToProps
  )(ResidentConsentFormsScreen)
)
